import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@/components/ui/overlays'
import { fromYmd, toYmd } from '@/data/meetings'
import type { MeetingDomain } from '@/data/types'
import { cn } from '@/lib/utils'

/**
 * A month grid rather than a date library.
 *
 * The whole requirement is Monday-first, one month at a time, with dates
 * outside the open domain disabled — picking last March while Upcoming is open
 * would otherwise silently do nothing. That is not worth a dependency, or the
 * second set of styling conventions one would bring with it.
 */
export function DatePicker({
  domain,
  value,
  onSelect,
  onClear,
}: {
  domain: MeetingDomain
  value: string | null
  onSelect: (ymd: string) => void
  onClear: () => void
}) {
  const today = useMemo(() => {
    const date = new Date()
    date.setHours(0, 0, 0, 0)
    return date
  }, [])
  const todayYmd = toYmd(today)

  const [month, setMonth] = useState(() => {
    const from = (value && fromYmd(value)) || today
    return new Date(from.getFullYear(), from.getMonth(), 1)
  })

  const cells = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1)
    const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate()
    const blanks = (first.getDay() + 6) % 7 // Monday-first
    const list: Array<Date | null> = Array.from({ length: blanks }, () => null)
    for (let day = 1; day <= days; day += 1) {
      list.push(new Date(month.getFullYear(), month.getMonth(), day))
    }
    while (list.length % 7 !== 0) list.push(null)
    return list
  }, [month])

  const weekdays = useMemo(() => {
    const monday = new Date(2024, 0, 1)
    return Array.from({ length: 7 }, (_, index) => {
      const day = new Date(monday)
      day.setDate(monday.getDate() + index)
      return day.toLocaleDateString(undefined, { weekday: 'narrow' })
    })
  }, [])

  /** Today belongs to Upcoming, so Past stops at yesterday. */
  const outOfDomain = (ymd: string) => (domain === 'upcoming' ? ymd < todayYmd : ymd >= todayYmd)

  const label = value
    ? (fromYmd(value) ?? today).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        ...(fromYmd(value)?.getFullYear() === today.getFullYear() ? {} : { year: 'numeric' }),
      })
    : 'Jump to date'

  return (
    <Popover>
      <PopoverTrigger
        className={cn(
          'inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-sm)] border px-2.5 text-xs outline-none',
          'transition-colors duration-100',
          value
            ? 'border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-fg)]'
            : 'border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]'
        )}
      >
        <CalendarDays className="size-3.5" />
        {label}
      </PopoverTrigger>

      <PopoverContent align="start" className="w-60 p-2">
        <div className="mb-1 flex items-center justify-between">
          <button
            type="button"
            aria-label="Previous month"
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
            className="grid size-6 place-items-center rounded-[var(--radius-sm)] text-[var(--color-fg-muted)] outline-none hover:bg-[var(--color-surface-hover)]"
          >
            <ChevronLeft className="size-3.5" />
          </button>
          <span className="text-xs font-medium text-[var(--color-fg)]">
            {month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
          </span>
          <button
            type="button"
            aria-label="Next month"
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
            className="grid size-6 place-items-center rounded-[var(--radius-sm)] text-[var(--color-fg-muted)] outline-none hover:bg-[var(--color-surface-hover)]"
          >
            <ChevronRight className="size-3.5" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-px">
          {weekdays.map((day, index) => (
            <span
              key={index}
              className="grid h-6 place-items-center text-2xs text-[var(--color-fg-subtle)]"
            >
              {day}
            </span>
          ))}

          {cells.map((day, index) => {
            if (!day) return <span key={index} />
            const ymd = toYmd(day)
            const disabled = outOfDomain(ymd)
            return (
              <PopoverClose
                key={index}
                render={
                  <button
                    type="button"
                    disabled={disabled}
                    aria-current={ymd === value ? 'date' : undefined}
                    onClick={() => onSelect(ymd)}
                    className={cn(
                      'tnum grid h-6 place-items-center rounded-[var(--radius-sm)] text-2xs outline-none',
                      disabled
                        ? 'text-[var(--color-fg-subtle)]/40'
                        : 'text-[var(--color-fg)] hover:bg-[var(--color-surface-hover)]',
                      ymd === todayYmd && !disabled && 'font-semibold text-[var(--color-primary)]',
                      ymd === value &&
                        'bg-[var(--color-primary)] text-[var(--color-primary-fg)] hover:bg-[var(--color-primary)]'
                    )}
                  >
                    {day.getDate()}
                  </button>
                }
              />
            )
          })}
        </div>

        {value ? (
          <PopoverClose
            render={
              <button
                type="button"
                onClick={onClear}
                className="mt-1 w-full rounded-[var(--radius-sm)] py-1 text-2xs text-[var(--color-fg-muted)] outline-none hover:bg-[var(--color-surface-hover)]"
              >
                Back to {domain === 'upcoming' ? 'next up' : 'most recent'}
              </button>
            }
          />
        ) : null}
      </PopoverContent>
    </Popover>
  )
}
